/**
 * The MCP auth gateway's self-service pages, framed by My Connections (#939, Feature H).
 *
 * The gateway serves /oauth/v2/manage/{credentials,consents,recent} and /signed-in. Framed with
 * `?embed=1&origin=…&theme=…`, each page posts messages to the Marketplace. The contract is
 * defined by the gateway (internal/manage/embed.go and templates/embed.html in mcp-auth-gateway):
 *
 *   {source: "mcp-gateway-manage", v: 1, page, type, …}
 *     ready   {count}         the page loaded and the user is signed in. A signed-out user is
 *                             sent to a sign-in page that refuses framing, so no `ready` ever
 *                             arrives for them.
 *     empty                   the list has nothing in it
 *     height  {height}        the page's height in CSS pixels, whenever it changes
 *     error   {reason,title}  something failed
 *     signed-in               (page "signed-in", from the sign-in popup) sign-in worked
 *
 * The gateway posts only to an origin it allows to frame it, and the Marketplace accepts a
 * message only from the gateway's own origin and from the window it is listening to.
 */

import { env } from "./env";

export const GATEWAY_MESSAGE_SOURCE = "mcp-gateway-manage";
export const GATEWAY_MESSAGE_VERSION = 1;

/** The framed list pages. */
export type ManagePage = "credentials" | "consents" | "recent";

/** Pages that can post a message. "error" is the gateway's own error page; "signed-in" is the popup. */
export type MessagePage = ManagePage | "error" | "signed-in";

export type GatewayMessage =
  | { type: "ready"; count: number }
  | { type: "empty" }
  | { type: "height"; height: number }
  | { type: "error"; reason: string; title: string }
  | { type: "signed-in" };

export interface ParsedGatewayMessage {
  page: MessagePage;
  message: GatewayMessage;
}

/** A page taller than this is treated as broken rather than growing the frame without limit. */
export const MAX_FRAME_HEIGHT = 20000;

const MESSAGE_PAGES: readonly MessagePage[] = ["credentials", "consents", "recent", "error", "signed-in"];
const MAX_TEXT_LENGTH = 200;

/**
 * The gateway's origin from REACT_APP_MCP_GATEWAY_URL, or null when it is unset or unusable.
 * Only https is accepted (plain http only for a local gateway), with no credentials, so a
 * mistyped value can't point the frame, or the origin that messages are trusted from, anywhere
 * unexpected.
 */
export function gatewayOrigin(): string | null {
  const raw = (env.REACT_APP_MCP_GATEWAY_URL ?? "").trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.username || url.password) return null;
  const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && local)) return null;
  return url.origin;
}

export type FrameTheme = "light" | "dark";

interface FrameUrlOptions {
  theme: FrameTheme;
  /** The server's URL from the MCP registry, for the recent-use page. */
  serverUrl?: string;
}

/** The address of a framed page, or null when no gateway is configured. */
export function manageFrameUrl(
  page: ManagePage,
  { theme, serverUrl }: FrameUrlOptions,
  pageOrigin: string = window.location.origin,
): string | null {
  const origin = gatewayOrigin();
  if (!origin) return null;
  const params = new URLSearchParams();
  if (page === "recent") {
    if (!serverUrl) return null;
    params.set("url", serverUrl);
  }
  params.set("embed", "1");
  params.set("origin", pageOrigin);
  params.set("theme", theme);
  return `${origin}/oauth/v2/manage/${page}?${params.toString()}`;
}

/**
 * A list page opened directly in its own tab (not framed), or null when no gateway is configured.
 * For when the frame can't be shown: the gateway's own page still works for the person.
 */
export function manageDirectUrl(page: "credentials" | "consents"): string | null {
  const origin = gatewayOrigin();
  return origin ? `${origin}/oauth/v2/manage/${page}` : null;
}

/** The sign-in popup's address, or null when no gateway is configured. */
export function signInUrl(theme: FrameTheme, pageOrigin: string = window.location.origin): string | null {
  const origin = gatewayOrigin();
  if (!origin) return null;
  const params = new URLSearchParams({ origin: pageOrigin, theme });
  return `${origin}/oauth/v2/manage/signed-in?${params.toString()}`;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.slice(0, MAX_TEXT_LENGTH) : "";
}

/**
 * Reads a window message from the gateway. Returns null for anything that isn't one: another
 * origin, another window, another sender's message, another format version, or a malformed
 * payload. The caller decides what it does with each type, so this never throws on odd input.
 *
 * `from` is the window the message must come from (the iframe's contentWindow, or the popup).
 * Origin alone isn't enough when two gateway windows are open at once.
 */
export function parseGatewayMessage(
  event: Pick<MessageEvent, "origin" | "source" | "data">,
  from: Window | null | undefined,
): ParsedGatewayMessage | null {
  const origin = gatewayOrigin();
  if (!origin || event.origin !== origin) return null;
  if (!from || event.source !== from) return null;

  const data: unknown = event.data;
  if (typeof data !== "object" || data === null) return null;
  const d = data as Record<string, unknown>;
  if (d.source !== GATEWAY_MESSAGE_SOURCE || d.v !== GATEWAY_MESSAGE_VERSION) return null;
  if (typeof d.page !== "string" || !(MESSAGE_PAGES as readonly string[]).includes(d.page)) return null;
  const page = d.page as MessagePage;

  switch (d.type) {
    case "ready": {
      const count = typeof d.count === "number" && Number.isFinite(d.count) && d.count >= 0 ? Math.floor(d.count) : 0;
      return { page, message: { type: "ready", count } };
    }
    case "empty":
      return { page, message: { type: "empty" } };
    case "height": {
      if (typeof d.height !== "number" || !Number.isFinite(d.height) || d.height < 0) return null;
      return { page, message: { type: "height", height: Math.min(Math.ceil(d.height), MAX_FRAME_HEIGHT) } };
    }
    case "error":
      return { page, message: { type: "error", reason: text(d.reason) || "load", title: text(d.title) || "Error" } };
    case "signed-in":
      return page === "signed-in" ? { page, message: { type: "signed-in" } } : null;
    default:
      return null;
  }
}
